import React, { useState } from "react";
import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

export default function FinalCTA() {
    const { t } = useT();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
            toast.error(t("finalCta.errorInvalid"));
            return;
        }
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            setEmail("");
            toast.success(t("finalCta.successTitle"), {
                description: t("finalCta.successBody"),
            });
        }, 700);
    };

    return (
        <section
            id="waitlist"
            data-testid={TID.finalCta.root}
            className="relative bg-sn-black text-sn-silver py-28 md:py-44 overflow-hidden"
        >
            <div className="absolute inset-0 hero-radial" />
            <div className="absolute inset-0 hero-grid opacity-60" />
            <div className="grain" />

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 items-end">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 1, ease: EASE }}
                    className="col-span-12 lg:col-span-8"
                >
                    <div className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue mb-8">
                        {t("finalCta.eyebrow")}
                    </div>
                    <h2 className="font-serif text-[13vw] md:text-[9vw] lg:text-[8rem] leading-[0.92] tracking-[-0.03em]">
                        {t("finalCta.headline_a")} <br />
                        <em className="italic text-sn-blue">
                            {t("finalCta.headline_b")}
                        </em>
                    </h2>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 1, delay: 0.15, ease: EASE }}
                    className="col-span-12 lg:col-span-4 lg:pb-6"
                >
                    <p className="text-sn-fog leading-relaxed max-w-md mb-8">
                        {t("finalCta.body")}
                    </p>
                    <form
                        onSubmit={handleSubmit}
                        noValidate
                        className="flex flex-col sm:flex-row gap-3 max-w-md"
                    >
                        <input
                            data-testid={TID.finalCta.input}
                            type="email"
                            placeholder={t("finalCta.placeholder")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 px-5 py-4 rounded-full bg-white/5 border border-white/15 text-sn-silver placeholder:text-sn-fog focus:outline-none focus:border-sn-blue transition-colors duration-300 font-mono-e text-xs uppercase tracking-widest"
                        />
                        <button
                            type="submit"
                            data-testid={TID.finalCta.submit}
                            disabled={loading}
                            className="btn-sweep btn-sweep-dark inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-sn-blue text-white font-mono-e text-[11px] uppercase tracking-[0.24em] transition-colors duration-300 disabled:opacity-60"
                        >
                            {loading ? t("finalCta.submitting") : t("finalCta.submit")}
                            {!loading && <ArrowUpRight size={16} />}
                        </button>
                    </form>
                </motion.div>
            </div>

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 mt-20 flex flex-wrap items-center gap-6 font-mono-e text-[10px] tracking-[0.25em] text-sn-fog uppercase">
                <span className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-sn-blue rounded-full animate-pulse" />
                    {t("finalCta.liveInfo")}
                </span>
                <span>·</span>
                <span>{t("finalCta.stores")}</span>
                <span>·</span>
                <span>{t("finalCta.noCard")}</span>
            </div>
        </section>
    );
}
